/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Document } from '../models/Document';
import type { LegacyFileConvert } from '../models/LegacyFileConvert';
import type { LegacyFileIgnore } from '../models/LegacyFileIgnore';
import type { PaginatedLegacyFileList } from '../models/PaginatedLegacyFileList';
import type { PaginatedProviderListList } from '../models/PaginatedProviderListList';
import type { PaginatedProviderNoteList } from '../models/PaginatedProviderNoteList';
import type { PatchedProviderStaffUpdate } from '../models/PatchedProviderStaffUpdate';
import type { PatchedScheduleTypeUpdate } from '../models/PatchedScheduleTypeUpdate';
import type { ProfessionalServicesAgreement } from '../models/ProfessionalServicesAgreement';
import type { ProviderDetail } from '../models/ProviderDetail';
import type { ProviderHistory } from '../models/ProviderHistory';
import type { ProviderList } from '../models/ProviderList';
import type { ProviderNote } from '../models/ProviderNote';
import type { ProviderScheduleBulkUpdate } from '../models/ProviderScheduleBulkUpdate';
import type { ProviderScheduleDatesResponse } from '../models/ProviderScheduleDatesResponse';
import type { ProviderStatusHistory } from '../models/ProviderStatusHistory';
import type { ScheduleTypeUpdate } from '../models/ScheduleTypeUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ProvidersService {

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns PaginatedProviderListList
     * @throws ApiError
     */
    public static providersList({
        createdAtAfter,
        createdAtBefore,
        hasImlc,
        limit,
        offset,
        ordering,
        profileDisposition,
        registrationStatus,
        search,
        sourceType,
        specialties,
        state,
        status,
        statusCategory,
        statusKind,
        updatedAtAfter,
        updatedAtBefore,
    }: {
        /**
         * Created
         */
        createdAtAfter?: string,
        /**
         * Created
         */
        createdAtBefore?: string,
        /**
         * Has IMLC
         */
        hasImlc?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Profile Disposition
         *
         * * `NOT_STARTED` - Not Started
         * * `IN_PROGRESS` - In Progress
         * * `1099_COMPLETE` - 1099 Complete
         * * `MKTPLC_COMPLETE` - Marketplace Complete
         * * `REMOVED` - Removed
         */
        profileDisposition?: Array<'1099_COMPLETE' | 'IN_PROGRESS' | 'MKTPLC_COMPLETE' | 'NOT_STARTED' | 'REMOVED' | null>,
        /**
         * Registration Status
         *
         * * `SETUP` - Setup
         * * `MISSING_INFO` - Missing Info
         * * `COMPLETE` - Complete
         * * `REJECTED` - Rejected
         */
        registrationStatus?: Array<'COMPLETE' | 'MISSING_INFO' | 'REJECTED' | 'SETUP'>,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Source Type
         *
         * * `ORGANIZATION` - Organization
         * * `MARKETPLACE` - Marketplace
         */
        sourceType?: Array<'MARKETPLACE' | 'ORGANIZATION' | null>,
        /**
         * Specialties
         *
         * * `ANESTHESIA` - Anesthesiology
         * * `CARDIOVASCULAR_DISEASE` - Cardiovascular Disease
         * * `COLON_AND_RECTAL_SURGERY` - Colon & Rectal Surgery
         * * `CRITICAL_CARE` - Critical Care Medicine
         * * `DERMATOLOGY` - Dermatology
         * * `EMERGENCY_MEDICINE` - Emergency Medicine
         * * `PRIMARY_CARE` - Family Medicine
         * * `GASTROENTEROLOGY` - Gastroenterology
         * * `GENERAL_PRACTICE` - General Practice
         * * `HOSPITAL_MEDICINE` - Hospital Medicine
         * * `INFECTIOUS_DISEASE` - Infectious Disease
         * * `INTERNAL_MEDICINE` - Internal Medicine
         * * `INTERVENTIONAL_CARDIOLOGY` - Interventional Cardiology
         * * `MEDICAL_ONCOLOGY` - Medical Oncology
         * * `NEPHROLOGY` - Nephrology
         * * `NEUROCRITICAL_CARE` - Neurocritical Care
         * * `NEUROLOGICAL_SURGERY` - Neurological Surgery
         * * `NEUROLOGY` - Neurology
         * * `OBSTETRICS_AND_GYNECOLOGY` - Obstetrics & Gynecology
         * * `OPHTHALMOLOGY` - Ophthalmology
         * * `ORAL_AND_MAXILLOFACIAL_SURGERY` - Oral & Maxillofacial Surgery
         * * `ORTHOPAEDIC_SURGERY` - Orthopaedic Surgery
         * * `OTOLARYNGOLOGY` - Otolaryngology
         * * `PAIN_MEDICINE` - Pain Medicine
         * * `PATHOLOGY` - Pathology
         * * `PEDIATRICS` - Pediatrics
         * * `PEDIATRIC_ANESTHESIA` - Pediatric Anesthesia
         * * `PEDIATRIC_CRITICAL_CARE` - Pediatric Critical Care
         * * `PEDIATRIC_EMERGENCY_MEDICINE` - Pediatric Emergency Medicine
         * * `PEDIATRIC_INFECTIOUS_DISEASES` - Pediatric Infectious Diseases
         * * `PHYSICAL_MEDICINE_AND_REHABILITATION` - Physical Medicine & Rehabilitation
         * * `PLASTIC_SURGERY` - Plastic Surgery
         * * `PREVENTIVE_MEDICINE` - Preventive Medicine
         * * `PULMONOLOGY` - Pulmonary Disease
         * * `PSYCHIATRY` - Psychiatry
         * * `RADIOLOGY` - Radiology
         * * `SURGERY` - Surgery
         * * `THORACIC_SURGERY` - Thoracic Surgery (Cardiothoracic Vascular Surgery)
         * * `TRANSPLANT_SURGERY` - Transplant Surgery
         * * `UROLOGY` - Urology
         * * `INTERVENTIONAL_RADIOLOGY` - Vascular & Interventional Radiology
         * * `OTHER` - Other
         */
        specialties?: Array<'ANESTHESIA' | 'CARDIOVASCULAR_DISEASE' | 'COLON_AND_RECTAL_SURGERY' | 'CRITICAL_CARE' | 'DERMATOLOGY' | 'EMERGENCY_MEDICINE' | 'GASTROENTEROLOGY' | 'GENERAL_PRACTICE' | 'HOSPITAL_MEDICINE' | 'INFECTIOUS_DISEASE' | 'INTERNAL_MEDICINE' | 'INTERVENTIONAL_CARDIOLOGY' | 'INTERVENTIONAL_RADIOLOGY' | 'MEDICAL_ONCOLOGY' | 'NEPHROLOGY' | 'NEUROCRITICAL_CARE' | 'NEUROLOGICAL_SURGERY' | 'NEUROLOGY' | 'OBSTETRICS_AND_GYNECOLOGY' | 'OPHTHALMOLOGY' | 'ORAL_AND_MAXILLOFACIAL_SURGERY' | 'ORTHOPAEDIC_SURGERY' | 'OTHER' | 'OTOLARYNGOLOGY' | 'PAIN_MEDICINE' | 'PATHOLOGY' | 'PEDIATRICS' | 'PEDIATRIC_ANESTHESIA' | 'PEDIATRIC_CRITICAL_CARE' | 'PEDIATRIC_EMERGENCY_MEDICINE' | 'PEDIATRIC_INFECTIOUS_DISEASES' | 'PHYSICAL_MEDICINE_AND_REHABILITATION' | 'PLASTIC_SURGERY' | 'PREVENTIVE_MEDICINE' | 'PRIMARY_CARE' | 'PSYCHIATRY' | 'PULMONOLOGY' | 'RADIOLOGY' | 'SURGERY' | 'THORACIC_SURGERY' | 'TRANSPLANT_SURGERY' | 'UROLOGY' | null>,
        /**
         * Home State
         *
         * * `AK` - Alaska
         * * `AL` - Alabama
         * * `AR` - Arkansas
         * * `AZ` - Arizona
         * * `CA` - California
         * * `CO` - Colorado
         * * `CT` - Connecticut
         * * `DC` - District of Columbia
         * * `DE` - Delaware
         * * `FL` - Florida
         * * `GA` - Georgia
         * * `HI` - Hawaii
         * * `IA` - Iowa
         * * `ID` - Idaho
         * * `IL` - Illinois
         * * `IN` - Indiana
         * * `KS` - Kansas
         * * `KY` - Kentucky
         * * `LA` - Louisiana
         * * `MA` - Massachusetts
         * * `MD` - Maryland
         * * `ME` - Maine
         * * `MI` - Michigan
         * * `MN` - Minnesota
         * * `MO` - Missouri
         * * `MS` - Mississippi
         * * `MT` - Montana
         * * `NC` - North Carolina
         * * `ND` - North Dakota
         * * `NE` - Nebraska
         * * `NH` - New Hampshire
         * * `NJ` - New Jersey
         * * `NM` - New Mexico
         * * `NV` - Nevada
         * * `NY` - New York
         * * `OH` - Ohio
         * * `OK` - Oklahoma
         * * `OR` - Oregon
         * * `PA` - Pennsylvania
         * * `RI` - Rhode Island
         * * `SC` - South Carolina
         * * `SD` - South Dakota
         * * `TN` - Tennessee
         * * `TX` - Texas
         * * `UT` - Utah
         * * `VA` - Virginia
         * * `VI` - Virgin Islands
         * * `VT` - Vermont
         * * `WA` - Washington
         * * `WI` - Wisconsin
         * * `WV` - West Virginia
         * * `WY` - Wyoming
         */
        state?: Array<'AK' | 'AL' | 'AR' | 'AZ' | 'CA' | 'CO' | 'CT' | 'DC' | 'DE' | 'FL' | 'GA' | 'HI' | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA' | 'MA' | 'MD' | 'ME' | 'MI' | 'MN' | 'MO' | 'MS' | 'MT' | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC' | 'SD' | 'TN' | 'TX' | 'UT' | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY'>,
        /**
         * Status
         */
        status?: Array<string | null>,
        /**
         * Status Category
         */
        statusCategory?: string,
        /**
         * Status Kind
         *
         * * `D` - Draft
         * * `I` - Initial
         * * `T` - Transitory
         * * `F` - Final
         */
        statusKind?: Array<'D' | 'F' | 'I' | 'T'>,
        /**
         * Updated
         */
        updatedAtAfter?: string,
        /**
         * Updated
         */
        updatedAtBefore?: string,
    }): CancelablePromise<PaginatedProviderListList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/',
            query: {
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'has_imlc': hasImlc,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'profile_disposition': profileDisposition,
                'registration_status': registrationStatus,
                'search': search,
                'source_type': sourceType,
                'specialties': specialties,
                'state': state,
                'status': status,
                'status_category': statusCategory,
                'status_kind': statusKind,
                'updated_at_after': updatedAtAfter,
                'updated_at_before': updatedAtBefore,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProviderDetail
     * @throws ApiError
     */
    public static providersRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
    }): CancelablePromise<ProviderDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * The few provider fields staff edit from the drawer (IMLC today); the
     * response is the full detail so the drawer refreshes from one payload.
     * @returns ProviderDetail
     * @throws ApiError
     */
    public static providersPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        requestBody?: PatchedProviderStaffUpdate,
    }): CancelablePromise<ProviderDetail> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/providers/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Handles the retrieval of change history for an instance.
     * The change history can be sorted in ascending or descending
     * order based on a query parameter.
     *
     * Args:
     * request: The HTTP request object.
     * *args: Additional positional arguments.
     * **kwargs: Additional keyword arguments.
     *
     * Returns:
     * Response: A Response object containing the aggregated and sorted change history
     * data for the instance.
     *
     * Note:
     * The `sort` query parameter is optional and determines the sort order of the
     * change history. Valid values are `asc` (ascending) and `desc` (descending).
     * @returns ProviderHistory
     * @throws ApiError
     */
    public static providersChangeHistoryRetrieve({
        id,
        sort,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * Sort direction. `asc` or `desc`
         */
        sort?: string,
    }): CancelablePromise<ProviderHistory> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/change-history/',
            path: {
                'id': id,
            },
            query: {
                'sort': sort,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProviderList
     * @throws ApiError
     */
    public static providersContractRetrieve({
        id,
        xTz,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * The timezone of the user making the request
         */
        xTz: string,
    }): CancelablePromise<ProviderList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/contract/',
            path: {
                'id': id,
            },
            headers: {
                'x-tz': xTz,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProfessionalServicesAgreement
     * @throws ApiError
     */
    public static providersContractCreate({
        id,
        xTz,
        requestBody,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * The timezone of the user making the request
         */
        xTz: string,
        requestBody: ProfessionalServicesAgreement,
    }): CancelablePromise<ProfessionalServicesAgreement> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/{id}/contract/',
            path: {
                'id': id,
            },
            headers: {
                'x-tz': xTz,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns binary
     * @throws ApiError
     */
    public static providersContractReviewDownloadRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/contract-review-download/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Every pre-documents-framework upload on the clinician that has not been
     * converted yet (the Documents tab's "Legacy uploads" section). Rows staff set
     * aside come back flagged ``is_ignored`` so the section can show and restore them.
     * @returns PaginatedLegacyFileList
     * @throws ApiError
     */
    public static providersLegacyFilesList({
        id,
        createdAtAfter,
        createdAtBefore,
        hasImlc,
        limit,
        offset,
        ordering,
        profileDisposition,
        registrationStatus,
        search,
        sourceType,
        specialties,
        state,
        status,
        statusCategory,
        statusKind,
        updatedAtAfter,
        updatedAtBefore,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * Created
         */
        createdAtAfter?: string,
        /**
         * Created
         */
        createdAtBefore?: string,
        /**
         * Has IMLC
         */
        hasImlc?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Profile Disposition
         *
         * * `NOT_STARTED` - Not Started
         * * `IN_PROGRESS` - In Progress
         * * `1099_COMPLETE` - 1099 Complete
         * * `MKTPLC_COMPLETE` - Marketplace Complete
         * * `REMOVED` - Removed
         */
        profileDisposition?: Array<'1099_COMPLETE' | 'IN_PROGRESS' | 'MKTPLC_COMPLETE' | 'NOT_STARTED' | 'REMOVED' | null>,
        /**
         * Registration Status
         *
         * * `SETUP` - Setup
         * * `MISSING_INFO` - Missing Info
         * * `COMPLETE` - Complete
         * * `REJECTED` - Rejected
         */
        registrationStatus?: Array<'COMPLETE' | 'MISSING_INFO' | 'REJECTED' | 'SETUP'>,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Source Type
         *
         * * `ORGANIZATION` - Organization
         * * `MARKETPLACE` - Marketplace
         */
        sourceType?: Array<'MARKETPLACE' | 'ORGANIZATION' | null>,
        /**
         * Specialties
         *
         * * `ANESTHESIA` - Anesthesiology
         * * `CARDIOVASCULAR_DISEASE` - Cardiovascular Disease
         * * `COLON_AND_RECTAL_SURGERY` - Colon & Rectal Surgery
         * * `CRITICAL_CARE` - Critical Care Medicine
         * * `DERMATOLOGY` - Dermatology
         * * `EMERGENCY_MEDICINE` - Emergency Medicine
         * * `PRIMARY_CARE` - Family Medicine
         * * `GASTROENTEROLOGY` - Gastroenterology
         * * `GENERAL_PRACTICE` - General Practice
         * * `HOSPITAL_MEDICINE` - Hospital Medicine
         * * `INFECTIOUS_DISEASE` - Infectious Disease
         * * `INTERNAL_MEDICINE` - Internal Medicine
         * * `INTERVENTIONAL_CARDIOLOGY` - Interventional Cardiology
         * * `MEDICAL_ONCOLOGY` - Medical Oncology
         * * `NEPHROLOGY` - Nephrology
         * * `NEUROCRITICAL_CARE` - Neurocritical Care
         * * `NEUROLOGICAL_SURGERY` - Neurological Surgery
         * * `NEUROLOGY` - Neurology
         * * `OBSTETRICS_AND_GYNECOLOGY` - Obstetrics & Gynecology
         * * `OPHTHALMOLOGY` - Ophthalmology
         * * `ORAL_AND_MAXILLOFACIAL_SURGERY` - Oral & Maxillofacial Surgery
         * * `ORTHOPAEDIC_SURGERY` - Orthopaedic Surgery
         * * `OTOLARYNGOLOGY` - Otolaryngology
         * * `PAIN_MEDICINE` - Pain Medicine
         * * `PATHOLOGY` - Pathology
         * * `PEDIATRICS` - Pediatrics
         * * `PEDIATRIC_ANESTHESIA` - Pediatric Anesthesia
         * * `PEDIATRIC_CRITICAL_CARE` - Pediatric Critical Care
         * * `PEDIATRIC_EMERGENCY_MEDICINE` - Pediatric Emergency Medicine
         * * `PEDIATRIC_INFECTIOUS_DISEASES` - Pediatric Infectious Diseases
         * * `PHYSICAL_MEDICINE_AND_REHABILITATION` - Physical Medicine & Rehabilitation
         * * `PLASTIC_SURGERY` - Plastic Surgery
         * * `PREVENTIVE_MEDICINE` - Preventive Medicine
         * * `PULMONOLOGY` - Pulmonary Disease
         * * `PSYCHIATRY` - Psychiatry
         * * `RADIOLOGY` - Radiology
         * * `SURGERY` - Surgery
         * * `THORACIC_SURGERY` - Thoracic Surgery (Cardiothoracic Vascular Surgery)
         * * `TRANSPLANT_SURGERY` - Transplant Surgery
         * * `UROLOGY` - Urology
         * * `INTERVENTIONAL_RADIOLOGY` - Vascular & Interventional Radiology
         * * `OTHER` - Other
         */
        specialties?: Array<'ANESTHESIA' | 'CARDIOVASCULAR_DISEASE' | 'COLON_AND_RECTAL_SURGERY' | 'CRITICAL_CARE' | 'DERMATOLOGY' | 'EMERGENCY_MEDICINE' | 'GASTROENTEROLOGY' | 'GENERAL_PRACTICE' | 'HOSPITAL_MEDICINE' | 'INFECTIOUS_DISEASE' | 'INTERNAL_MEDICINE' | 'INTERVENTIONAL_CARDIOLOGY' | 'INTERVENTIONAL_RADIOLOGY' | 'MEDICAL_ONCOLOGY' | 'NEPHROLOGY' | 'NEUROCRITICAL_CARE' | 'NEUROLOGICAL_SURGERY' | 'NEUROLOGY' | 'OBSTETRICS_AND_GYNECOLOGY' | 'OPHTHALMOLOGY' | 'ORAL_AND_MAXILLOFACIAL_SURGERY' | 'ORTHOPAEDIC_SURGERY' | 'OTHER' | 'OTOLARYNGOLOGY' | 'PAIN_MEDICINE' | 'PATHOLOGY' | 'PEDIATRICS' | 'PEDIATRIC_ANESTHESIA' | 'PEDIATRIC_CRITICAL_CARE' | 'PEDIATRIC_EMERGENCY_MEDICINE' | 'PEDIATRIC_INFECTIOUS_DISEASES' | 'PHYSICAL_MEDICINE_AND_REHABILITATION' | 'PLASTIC_SURGERY' | 'PREVENTIVE_MEDICINE' | 'PRIMARY_CARE' | 'PSYCHIATRY' | 'PULMONOLOGY' | 'RADIOLOGY' | 'SURGERY' | 'THORACIC_SURGERY' | 'TRANSPLANT_SURGERY' | 'UROLOGY' | null>,
        /**
         * Home State
         *
         * * `AK` - Alaska
         * * `AL` - Alabama
         * * `AR` - Arkansas
         * * `AZ` - Arizona
         * * `CA` - California
         * * `CO` - Colorado
         * * `CT` - Connecticut
         * * `DC` - District of Columbia
         * * `DE` - Delaware
         * * `FL` - Florida
         * * `GA` - Georgia
         * * `HI` - Hawaii
         * * `IA` - Iowa
         * * `ID` - Idaho
         * * `IL` - Illinois
         * * `IN` - Indiana
         * * `KS` - Kansas
         * * `KY` - Kentucky
         * * `LA` - Louisiana
         * * `MA` - Massachusetts
         * * `MD` - Maryland
         * * `ME` - Maine
         * * `MI` - Michigan
         * * `MN` - Minnesota
         * * `MO` - Missouri
         * * `MS` - Mississippi
         * * `MT` - Montana
         * * `NC` - North Carolina
         * * `ND` - North Dakota
         * * `NE` - Nebraska
         * * `NH` - New Hampshire
         * * `NJ` - New Jersey
         * * `NM` - New Mexico
         * * `NV` - Nevada
         * * `NY` - New York
         * * `OH` - Ohio
         * * `OK` - Oklahoma
         * * `OR` - Oregon
         * * `PA` - Pennsylvania
         * * `RI` - Rhode Island
         * * `SC` - South Carolina
         * * `SD` - South Dakota
         * * `TN` - Tennessee
         * * `TX` - Texas
         * * `UT` - Utah
         * * `VA` - Virginia
         * * `VI` - Virgin Islands
         * * `VT` - Vermont
         * * `WA` - Washington
         * * `WI` - Wisconsin
         * * `WV` - West Virginia
         * * `WY` - Wyoming
         */
        state?: Array<'AK' | 'AL' | 'AR' | 'AZ' | 'CA' | 'CO' | 'CT' | 'DC' | 'DE' | 'FL' | 'GA' | 'HI' | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA' | 'MA' | 'MD' | 'ME' | 'MI' | 'MN' | 'MO' | 'MS' | 'MT' | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC' | 'SD' | 'TN' | 'TX' | 'UT' | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY'>,
        /**
         * Status
         */
        status?: Array<string | null>,
        /**
         * Status Category
         */
        statusCategory?: string,
        /**
         * Status Kind
         *
         * * `D` - Draft
         * * `I` - Initial
         * * `T` - Transitory
         * * `F` - Final
         */
        statusKind?: Array<'D' | 'F' | 'I' | 'T'>,
        /**
         * Updated
         */
        updatedAtAfter?: string,
        /**
         * Updated
         */
        updatedAtBefore?: string,
    }): CancelablePromise<PaginatedLegacyFileList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/legacy-files/',
            path: {
                'id': id,
            },
            query: {
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'has_imlc': hasImlc,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'profile_disposition': profileDisposition,
                'registration_status': registrationStatus,
                'search': search,
                'source_type': sourceType,
                'specialties': specialties,
                'state': state,
                'status': status,
                'status_category': statusCategory,
                'status_kind': statusKind,
                'updated_at_after': updatedAtAfter,
                'updated_at_before': updatedAtBefore,
            },
        });
    }

    /**
     * Copy one legacy upload into a confirmed Document of the chosen type; a
     * credential file is linked to its entry. Returns the new document.
     * @returns Document
     * @throws ApiError
     */
    public static providersLegacyFilesConvertCreate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        requestBody: LegacyFileConvert,
    }): CancelablePromise<Document> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/{id}/legacy-files/convert/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set one legacy upload aside (moved by hand / not to be converted) or restore
     * it. An ignored file leaves the pending list and every conversion pass.
     * @returns void
     * @throws ApiError
     */
    public static providersLegacyFilesIgnoreCreate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        requestBody: LegacyFileIgnore,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/{id}/legacy-files/ignore/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns PaginatedProviderNoteList
     * @returns ProviderNote
     * @throws ApiError
     */
    public static providersNotesList({
        id,
        createdAtAfter,
        createdAtBefore,
        hasImlc,
        limit,
        offset,
        ordering,
        profileDisposition,
        registrationStatus,
        search,
        sourceType,
        specialties,
        state,
        status,
        statusCategory,
        statusKind,
        updatedAtAfter,
        updatedAtBefore,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * Created
         */
        createdAtAfter?: string,
        /**
         * Created
         */
        createdAtBefore?: string,
        /**
         * Has IMLC
         */
        hasImlc?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Profile Disposition
         *
         * * `NOT_STARTED` - Not Started
         * * `IN_PROGRESS` - In Progress
         * * `1099_COMPLETE` - 1099 Complete
         * * `MKTPLC_COMPLETE` - Marketplace Complete
         * * `REMOVED` - Removed
         */
        profileDisposition?: Array<'1099_COMPLETE' | 'IN_PROGRESS' | 'MKTPLC_COMPLETE' | 'NOT_STARTED' | 'REMOVED' | null>,
        /**
         * Registration Status
         *
         * * `SETUP` - Setup
         * * `MISSING_INFO` - Missing Info
         * * `COMPLETE` - Complete
         * * `REJECTED` - Rejected
         */
        registrationStatus?: Array<'COMPLETE' | 'MISSING_INFO' | 'REJECTED' | 'SETUP'>,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Source Type
         *
         * * `ORGANIZATION` - Organization
         * * `MARKETPLACE` - Marketplace
         */
        sourceType?: Array<'MARKETPLACE' | 'ORGANIZATION' | null>,
        /**
         * Specialties
         *
         * * `ANESTHESIA` - Anesthesiology
         * * `CARDIOVASCULAR_DISEASE` - Cardiovascular Disease
         * * `COLON_AND_RECTAL_SURGERY` - Colon & Rectal Surgery
         * * `CRITICAL_CARE` - Critical Care Medicine
         * * `DERMATOLOGY` - Dermatology
         * * `EMERGENCY_MEDICINE` - Emergency Medicine
         * * `PRIMARY_CARE` - Family Medicine
         * * `GASTROENTEROLOGY` - Gastroenterology
         * * `GENERAL_PRACTICE` - General Practice
         * * `HOSPITAL_MEDICINE` - Hospital Medicine
         * * `INFECTIOUS_DISEASE` - Infectious Disease
         * * `INTERNAL_MEDICINE` - Internal Medicine
         * * `INTERVENTIONAL_CARDIOLOGY` - Interventional Cardiology
         * * `MEDICAL_ONCOLOGY` - Medical Oncology
         * * `NEPHROLOGY` - Nephrology
         * * `NEUROCRITICAL_CARE` - Neurocritical Care
         * * `NEUROLOGICAL_SURGERY` - Neurological Surgery
         * * `NEUROLOGY` - Neurology
         * * `OBSTETRICS_AND_GYNECOLOGY` - Obstetrics & Gynecology
         * * `OPHTHALMOLOGY` - Ophthalmology
         * * `ORAL_AND_MAXILLOFACIAL_SURGERY` - Oral & Maxillofacial Surgery
         * * `ORTHOPAEDIC_SURGERY` - Orthopaedic Surgery
         * * `OTOLARYNGOLOGY` - Otolaryngology
         * * `PAIN_MEDICINE` - Pain Medicine
         * * `PATHOLOGY` - Pathology
         * * `PEDIATRICS` - Pediatrics
         * * `PEDIATRIC_ANESTHESIA` - Pediatric Anesthesia
         * * `PEDIATRIC_CRITICAL_CARE` - Pediatric Critical Care
         * * `PEDIATRIC_EMERGENCY_MEDICINE` - Pediatric Emergency Medicine
         * * `PEDIATRIC_INFECTIOUS_DISEASES` - Pediatric Infectious Diseases
         * * `PHYSICAL_MEDICINE_AND_REHABILITATION` - Physical Medicine & Rehabilitation
         * * `PLASTIC_SURGERY` - Plastic Surgery
         * * `PREVENTIVE_MEDICINE` - Preventive Medicine
         * * `PULMONOLOGY` - Pulmonary Disease
         * * `PSYCHIATRY` - Psychiatry
         * * `RADIOLOGY` - Radiology
         * * `SURGERY` - Surgery
         * * `THORACIC_SURGERY` - Thoracic Surgery (Cardiothoracic Vascular Surgery)
         * * `TRANSPLANT_SURGERY` - Transplant Surgery
         * * `UROLOGY` - Urology
         * * `INTERVENTIONAL_RADIOLOGY` - Vascular & Interventional Radiology
         * * `OTHER` - Other
         */
        specialties?: Array<'ANESTHESIA' | 'CARDIOVASCULAR_DISEASE' | 'COLON_AND_RECTAL_SURGERY' | 'CRITICAL_CARE' | 'DERMATOLOGY' | 'EMERGENCY_MEDICINE' | 'GASTROENTEROLOGY' | 'GENERAL_PRACTICE' | 'HOSPITAL_MEDICINE' | 'INFECTIOUS_DISEASE' | 'INTERNAL_MEDICINE' | 'INTERVENTIONAL_CARDIOLOGY' | 'INTERVENTIONAL_RADIOLOGY' | 'MEDICAL_ONCOLOGY' | 'NEPHROLOGY' | 'NEUROCRITICAL_CARE' | 'NEUROLOGICAL_SURGERY' | 'NEUROLOGY' | 'OBSTETRICS_AND_GYNECOLOGY' | 'OPHTHALMOLOGY' | 'ORAL_AND_MAXILLOFACIAL_SURGERY' | 'ORTHOPAEDIC_SURGERY' | 'OTHER' | 'OTOLARYNGOLOGY' | 'PAIN_MEDICINE' | 'PATHOLOGY' | 'PEDIATRICS' | 'PEDIATRIC_ANESTHESIA' | 'PEDIATRIC_CRITICAL_CARE' | 'PEDIATRIC_EMERGENCY_MEDICINE' | 'PEDIATRIC_INFECTIOUS_DISEASES' | 'PHYSICAL_MEDICINE_AND_REHABILITATION' | 'PLASTIC_SURGERY' | 'PREVENTIVE_MEDICINE' | 'PRIMARY_CARE' | 'PSYCHIATRY' | 'PULMONOLOGY' | 'RADIOLOGY' | 'SURGERY' | 'THORACIC_SURGERY' | 'TRANSPLANT_SURGERY' | 'UROLOGY' | null>,
        /**
         * Home State
         *
         * * `AK` - Alaska
         * * `AL` - Alabama
         * * `AR` - Arkansas
         * * `AZ` - Arizona
         * * `CA` - California
         * * `CO` - Colorado
         * * `CT` - Connecticut
         * * `DC` - District of Columbia
         * * `DE` - Delaware
         * * `FL` - Florida
         * * `GA` - Georgia
         * * `HI` - Hawaii
         * * `IA` - Iowa
         * * `ID` - Idaho
         * * `IL` - Illinois
         * * `IN` - Indiana
         * * `KS` - Kansas
         * * `KY` - Kentucky
         * * `LA` - Louisiana
         * * `MA` - Massachusetts
         * * `MD` - Maryland
         * * `ME` - Maine
         * * `MI` - Michigan
         * * `MN` - Minnesota
         * * `MO` - Missouri
         * * `MS` - Mississippi
         * * `MT` - Montana
         * * `NC` - North Carolina
         * * `ND` - North Dakota
         * * `NE` - Nebraska
         * * `NH` - New Hampshire
         * * `NJ` - New Jersey
         * * `NM` - New Mexico
         * * `NV` - Nevada
         * * `NY` - New York
         * * `OH` - Ohio
         * * `OK` - Oklahoma
         * * `OR` - Oregon
         * * `PA` - Pennsylvania
         * * `RI` - Rhode Island
         * * `SC` - South Carolina
         * * `SD` - South Dakota
         * * `TN` - Tennessee
         * * `TX` - Texas
         * * `UT` - Utah
         * * `VA` - Virginia
         * * `VI` - Virgin Islands
         * * `VT` - Vermont
         * * `WA` - Washington
         * * `WI` - Wisconsin
         * * `WV` - West Virginia
         * * `WY` - Wyoming
         */
        state?: Array<'AK' | 'AL' | 'AR' | 'AZ' | 'CA' | 'CO' | 'CT' | 'DC' | 'DE' | 'FL' | 'GA' | 'HI' | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA' | 'MA' | 'MD' | 'ME' | 'MI' | 'MN' | 'MO' | 'MS' | 'MT' | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC' | 'SD' | 'TN' | 'TX' | 'UT' | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY'>,
        /**
         * Status
         */
        status?: Array<string | null>,
        /**
         * Status Category
         */
        statusCategory?: string,
        /**
         * Status Kind
         *
         * * `D` - Draft
         * * `I` - Initial
         * * `T` - Transitory
         * * `F` - Final
         */
        statusKind?: Array<'D' | 'F' | 'I' | 'T'>,
        /**
         * Updated
         */
        updatedAtAfter?: string,
        /**
         * Updated
         */
        updatedAtBefore?: string,
    }): CancelablePromise<PaginatedProviderNoteList | ProviderNote> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/notes/',
            path: {
                'id': id,
            },
            query: {
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'has_imlc': hasImlc,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'profile_disposition': profileDisposition,
                'registration_status': registrationStatus,
                'search': search,
                'source_type': sourceType,
                'specialties': specialties,
                'state': state,
                'status': status,
                'status_category': statusCategory,
                'status_kind': statusKind,
                'updated_at_after': updatedAtAfter,
                'updated_at_before': updatedAtBefore,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns PaginatedProviderNoteList
     * @returns ProviderNote
     * @throws ApiError
     */
    public static providersNotesCreate({
        id,
        requestBody,
        createdAtAfter,
        createdAtBefore,
        hasImlc,
        limit,
        offset,
        ordering,
        profileDisposition,
        registrationStatus,
        search,
        sourceType,
        specialties,
        state,
        status,
        statusCategory,
        statusKind,
        updatedAtAfter,
        updatedAtBefore,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        requestBody: ProviderNote,
        /**
         * Created
         */
        createdAtAfter?: string,
        /**
         * Created
         */
        createdAtBefore?: string,
        /**
         * Has IMLC
         */
        hasImlc?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Profile Disposition
         *
         * * `NOT_STARTED` - Not Started
         * * `IN_PROGRESS` - In Progress
         * * `1099_COMPLETE` - 1099 Complete
         * * `MKTPLC_COMPLETE` - Marketplace Complete
         * * `REMOVED` - Removed
         */
        profileDisposition?: Array<'1099_COMPLETE' | 'IN_PROGRESS' | 'MKTPLC_COMPLETE' | 'NOT_STARTED' | 'REMOVED' | null>,
        /**
         * Registration Status
         *
         * * `SETUP` - Setup
         * * `MISSING_INFO` - Missing Info
         * * `COMPLETE` - Complete
         * * `REJECTED` - Rejected
         */
        registrationStatus?: Array<'COMPLETE' | 'MISSING_INFO' | 'REJECTED' | 'SETUP'>,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Source Type
         *
         * * `ORGANIZATION` - Organization
         * * `MARKETPLACE` - Marketplace
         */
        sourceType?: Array<'MARKETPLACE' | 'ORGANIZATION' | null>,
        /**
         * Specialties
         *
         * * `ANESTHESIA` - Anesthesiology
         * * `CARDIOVASCULAR_DISEASE` - Cardiovascular Disease
         * * `COLON_AND_RECTAL_SURGERY` - Colon & Rectal Surgery
         * * `CRITICAL_CARE` - Critical Care Medicine
         * * `DERMATOLOGY` - Dermatology
         * * `EMERGENCY_MEDICINE` - Emergency Medicine
         * * `PRIMARY_CARE` - Family Medicine
         * * `GASTROENTEROLOGY` - Gastroenterology
         * * `GENERAL_PRACTICE` - General Practice
         * * `HOSPITAL_MEDICINE` - Hospital Medicine
         * * `INFECTIOUS_DISEASE` - Infectious Disease
         * * `INTERNAL_MEDICINE` - Internal Medicine
         * * `INTERVENTIONAL_CARDIOLOGY` - Interventional Cardiology
         * * `MEDICAL_ONCOLOGY` - Medical Oncology
         * * `NEPHROLOGY` - Nephrology
         * * `NEUROCRITICAL_CARE` - Neurocritical Care
         * * `NEUROLOGICAL_SURGERY` - Neurological Surgery
         * * `NEUROLOGY` - Neurology
         * * `OBSTETRICS_AND_GYNECOLOGY` - Obstetrics & Gynecology
         * * `OPHTHALMOLOGY` - Ophthalmology
         * * `ORAL_AND_MAXILLOFACIAL_SURGERY` - Oral & Maxillofacial Surgery
         * * `ORTHOPAEDIC_SURGERY` - Orthopaedic Surgery
         * * `OTOLARYNGOLOGY` - Otolaryngology
         * * `PAIN_MEDICINE` - Pain Medicine
         * * `PATHOLOGY` - Pathology
         * * `PEDIATRICS` - Pediatrics
         * * `PEDIATRIC_ANESTHESIA` - Pediatric Anesthesia
         * * `PEDIATRIC_CRITICAL_CARE` - Pediatric Critical Care
         * * `PEDIATRIC_EMERGENCY_MEDICINE` - Pediatric Emergency Medicine
         * * `PEDIATRIC_INFECTIOUS_DISEASES` - Pediatric Infectious Diseases
         * * `PHYSICAL_MEDICINE_AND_REHABILITATION` - Physical Medicine & Rehabilitation
         * * `PLASTIC_SURGERY` - Plastic Surgery
         * * `PREVENTIVE_MEDICINE` - Preventive Medicine
         * * `PULMONOLOGY` - Pulmonary Disease
         * * `PSYCHIATRY` - Psychiatry
         * * `RADIOLOGY` - Radiology
         * * `SURGERY` - Surgery
         * * `THORACIC_SURGERY` - Thoracic Surgery (Cardiothoracic Vascular Surgery)
         * * `TRANSPLANT_SURGERY` - Transplant Surgery
         * * `UROLOGY` - Urology
         * * `INTERVENTIONAL_RADIOLOGY` - Vascular & Interventional Radiology
         * * `OTHER` - Other
         */
        specialties?: Array<'ANESTHESIA' | 'CARDIOVASCULAR_DISEASE' | 'COLON_AND_RECTAL_SURGERY' | 'CRITICAL_CARE' | 'DERMATOLOGY' | 'EMERGENCY_MEDICINE' | 'GASTROENTEROLOGY' | 'GENERAL_PRACTICE' | 'HOSPITAL_MEDICINE' | 'INFECTIOUS_DISEASE' | 'INTERNAL_MEDICINE' | 'INTERVENTIONAL_CARDIOLOGY' | 'INTERVENTIONAL_RADIOLOGY' | 'MEDICAL_ONCOLOGY' | 'NEPHROLOGY' | 'NEUROCRITICAL_CARE' | 'NEUROLOGICAL_SURGERY' | 'NEUROLOGY' | 'OBSTETRICS_AND_GYNECOLOGY' | 'OPHTHALMOLOGY' | 'ORAL_AND_MAXILLOFACIAL_SURGERY' | 'ORTHOPAEDIC_SURGERY' | 'OTHER' | 'OTOLARYNGOLOGY' | 'PAIN_MEDICINE' | 'PATHOLOGY' | 'PEDIATRICS' | 'PEDIATRIC_ANESTHESIA' | 'PEDIATRIC_CRITICAL_CARE' | 'PEDIATRIC_EMERGENCY_MEDICINE' | 'PEDIATRIC_INFECTIOUS_DISEASES' | 'PHYSICAL_MEDICINE_AND_REHABILITATION' | 'PLASTIC_SURGERY' | 'PREVENTIVE_MEDICINE' | 'PRIMARY_CARE' | 'PSYCHIATRY' | 'PULMONOLOGY' | 'RADIOLOGY' | 'SURGERY' | 'THORACIC_SURGERY' | 'TRANSPLANT_SURGERY' | 'UROLOGY' | null>,
        /**
         * Home State
         *
         * * `AK` - Alaska
         * * `AL` - Alabama
         * * `AR` - Arkansas
         * * `AZ` - Arizona
         * * `CA` - California
         * * `CO` - Colorado
         * * `CT` - Connecticut
         * * `DC` - District of Columbia
         * * `DE` - Delaware
         * * `FL` - Florida
         * * `GA` - Georgia
         * * `HI` - Hawaii
         * * `IA` - Iowa
         * * `ID` - Idaho
         * * `IL` - Illinois
         * * `IN` - Indiana
         * * `KS` - Kansas
         * * `KY` - Kentucky
         * * `LA` - Louisiana
         * * `MA` - Massachusetts
         * * `MD` - Maryland
         * * `ME` - Maine
         * * `MI` - Michigan
         * * `MN` - Minnesota
         * * `MO` - Missouri
         * * `MS` - Mississippi
         * * `MT` - Montana
         * * `NC` - North Carolina
         * * `ND` - North Dakota
         * * `NE` - Nebraska
         * * `NH` - New Hampshire
         * * `NJ` - New Jersey
         * * `NM` - New Mexico
         * * `NV` - Nevada
         * * `NY` - New York
         * * `OH` - Ohio
         * * `OK` - Oklahoma
         * * `OR` - Oregon
         * * `PA` - Pennsylvania
         * * `RI` - Rhode Island
         * * `SC` - South Carolina
         * * `SD` - South Dakota
         * * `TN` - Tennessee
         * * `TX` - Texas
         * * `UT` - Utah
         * * `VA` - Virginia
         * * `VI` - Virgin Islands
         * * `VT` - Vermont
         * * `WA` - Washington
         * * `WI` - Wisconsin
         * * `WV` - West Virginia
         * * `WY` - Wyoming
         */
        state?: Array<'AK' | 'AL' | 'AR' | 'AZ' | 'CA' | 'CO' | 'CT' | 'DC' | 'DE' | 'FL' | 'GA' | 'HI' | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA' | 'MA' | 'MD' | 'ME' | 'MI' | 'MN' | 'MO' | 'MS' | 'MT' | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC' | 'SD' | 'TN' | 'TX' | 'UT' | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY'>,
        /**
         * Status
         */
        status?: Array<string | null>,
        /**
         * Status Category
         */
        statusCategory?: string,
        /**
         * Status Kind
         *
         * * `D` - Draft
         * * `I` - Initial
         * * `T` - Transitory
         * * `F` - Final
         */
        statusKind?: Array<'D' | 'F' | 'I' | 'T'>,
        /**
         * Updated
         */
        updatedAtAfter?: string,
        /**
         * Updated
         */
        updatedAtBefore?: string,
    }): CancelablePromise<PaginatedProviderNoteList | ProviderNote> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/{id}/notes/',
            path: {
                'id': id,
            },
            query: {
                'created_at_after': createdAtAfter,
                'created_at_before': createdAtBefore,
                'has_imlc': hasImlc,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'profile_disposition': profileDisposition,
                'registration_status': registrationStatus,
                'search': search,
                'source_type': sourceType,
                'specialties': specialties,
                'state': state,
                'status': status,
                'status_category': statusCategory,
                'status_kind': statusKind,
                'updated_at_after': updatedAtAfter,
                'updated_at_before': updatedAtBefore,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Read-only: provider statuses are driven by the workflow rules, so the
     * `StatusUpdateMixin` (which also exposes `status-update`) is not mixed in.
     * @returns ProviderStatusHistory
     * @throws ApiError
     */
    public static providersStatusHistoryRetrieve({
        id,
        sort,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * Sort direction. `asc` or `desc`
         */
        sort?: string,
    }): CancelablePromise<ProviderStatusHistory> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/status-history/',
            path: {
                'id': id,
            },
            query: {
                'sort': sort,
            },
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns void
     * @throws ApiError
     */
    public static providersScheduleBulkCreate({
        requestBody,
    }: {
        requestBody: ProviderScheduleBulkUpdate,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/schedule/bulk/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns ProviderScheduleDatesResponse
     * @throws ApiError
     */
    public static providersScheduleDatesRetrieve({
        end,
        start,
    }: {
        /**
         * Window end, inclusive (YYYY-MM-DD).
         */
        end: string,
        /**
         * Window start, inclusive (YYYY-MM-DD).
         */
        start: string,
    }): CancelablePromise<ProviderScheduleDatesResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/schedule/dates/',
            query: {
                'end': end,
                'start': start,
            },
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns ScheduleTypeUpdate
     * @throws ApiError
     */
    public static providersScheduleTypePartialUpdate({
        requestBody,
    }: {
        requestBody?: PatchedScheduleTypeUpdate,
    }): CancelablePromise<ScheduleTypeUpdate> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/providers/schedule/type/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
